import { BaseEffect } from '../BaseEffect.js';

/**
 * Scanline effect implementation
 */
export class ScanlineEffect extends BaseEffect {
    constructor(shaderManager) {
        const defaultParams = {
            scanlineIntensity: 0.3,
            scanlineDensity: 100,
            scanlineSpeed: 7.5,
            brightness: 1.2,
            animate: true
        };

        super('scanlines', shaderManager, defaultParams);
    }

    /**
     * Initialize the effect
     */
    async init() {
        const vertexSource = `
            attribute vec2 a_position;
            attribute vec2 a_texCoord;
            varying vec2 v_texcoord;
            
            void main() {
                gl_Position = vec4(a_position, 0, 1);
                v_texcoord = a_texCoord;
            }
        `;

        const fragmentSource = `
            precision mediump float;
            
            uniform sampler2D u_image;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_scanlineIntensity;
            uniform float u_scanlineDensity;
            uniform float u_scanlineSpeed;
            uniform float u_brightness;
            
            varying vec2 v_texcoord;
            
            void main() {
                // Calculate aspect-correct UV coordinates
                vec2 uv = v_texcoord;
                float aspect = u_resolution.x / u_resolution.y;
                
                vec4 color = texture2D(u_image, uv);
                
                // Calculate scanline effect with aspect ratio correction
                float scanline = sin(uv.y * u_resolution.y * (u_scanlineDensity / 100.0) + u_time * u_scanlineSpeed) * 0.5 + 0.5;
                scanline = mix(1.0, scanline, u_scanlineIntensity);
                
                // Apply brightness adjustment
                color.rgb *= u_brightness;
                
                // Apply scanline effect
                color.rgb *= scanline;
                
                gl_FragColor = color;
            }
        `;

        await super.init(vertexSource, fragmentSource);
    }

    /**
     * Set up effect-specific uniforms
     */
    setupUniforms() {
        super.setupUniforms();
        
        // Let BaseEffect handle u_resolution
        this.addUniform('u_scanlineIntensity', { type: '1f' });
        this.addUniform('u_scanlineDensity', { type: '1f' });
        this.addUniform('u_scanlineSpeed', { type: '1f' });
        this.addUniform('u_brightness', { type: '1f' });
    }

    /**
     * Update effect-specific uniforms
     * @param {Object} uniforms - Uniform values to update
     */
    updateUniforms(uniforms) {
        // Call parent class to handle common uniforms like u_resolution
        super.updateUniforms(uniforms);
        
        Object.assign(uniforms, {
            u_scanlineIntensity: { value: this.params.scanlineIntensity, type: '1f' },
            u_scanlineDensity: { value: this.params.scanlineDensity, type: '1f' },
            u_scanlineSpeed: { value: this.params.scanlineSpeed, type: '1f' },
            u_brightness: { value: this.params.brightness, type: '1f' }
        });

        // Update time value based on animation state
        if (!this.params.animate) {
            uniforms.u_time.value = 0;
        }
    }

    /**
     * Get UI configuration for parameters
     * @returns {Object} UI configuration
     */
    getUIConfig() {
        return {
            scanlineIntensity: {
                min: 0,
                max: 1,
                step: 0.01,
                label: 'Scanline Intensity'
            },
            scanlineDensity: {
                min: 50,
                max: 200,
                step: 1,
                label: 'Scanline Density'
            },
            scanlineSpeed: {
                min: 0,
                max: 50,
                step: 0.5,
                label: 'Scanline Speed'
            },
            brightness: {
                min: 0.5,
                max: 2,
                step: 0.1,
                label: 'Brightness'
            },
            animate: {
                label: 'Animate'
            }
        };
    }
} 